'use client';

import { useId } from 'react';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Большое поле ввода со счётчиком и очисткой.
 *
 * Один компонент на все длинные поля витрины: форма рецепта, проверка и
 * правка текста, озвучка, конструктор. Копии здесь недопустимы не из
 * экономии строк — счётчик и крестик стоят внутри поля, поверх текста, а
 * это требует согласованных отступов у обоих. Разойдись они, надпись
 * наехала бы на первую строку, и заметно это стало бы только на длинном
 * тексте, то есть у человека, а не у нас.
 *
 * Зачем счётчик. Длинный текст здесь обычная вещь: люди несут главы
 * дипломов и статьи. Без счётчика нельзя ни оценить объём, ни понять,
 * сколько осталось до предела, а предел у части полей есть
 * (`maxLength`), и упереться в него молча — худшее из поведений.
 *
 * Зачем крестик. Поле переиспользуется под вторую задачу сразу: вставил
 * другой текст — надо стереть прежний. Выделять двадцать тысяч знаков
 * пальцем на телефоне мучительно, а Ctrl+A там попросту нет.
 */

/** Число слов: по непробельным отрезкам, а не по разделителям. */
export function kolichestvoSlov(tekst: string): number {
  const obrezannyj = tekst.trim();
  if (obrezannyj === '') return 0;
  return obrezannyj.split(/\s+/u).length;
}

export function PoleTeksta({
  value,
  onChange,
  id,
  rows = 6,
  maxLength,
  placeholder,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  rows?: number;
  maxLength?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Оформление самого поля: рамка и фон у витрины и форм разные. */
  className?: string;
  'aria-label'?: string;
}) {
  // Свой идентификатор, когда снаружи не дали: счётчик связывается с
  // полем через aria-describedby, а без идентификатора связи нет.
  const svoj = useId();
  const idPolya = id ?? `pole-${svoj}`;
  const idSchyotchika = `${idPolya}-schyotchik`;

  // Подписи из словаря, а не строками в коде: витрина живёт на четырёх
  // языках, и «Очистить поле» кириллицей на испанской странице — тот же
  // изъян, из-за которого убрали заготовки раздела «Сообщество».
  const t = useTranslations('ais_pole');

  const znakov = value.length;
  const slov = kolichestvoSlov(value);
  const pusto = value === '';

  return (
    <div className="relative">
      {/*
        Строка служебных подписей лежит поверх поля, а не над ним: место
        над полем занято его подписью, и второй ряд надписей отодвигал бы
        само поле вниз на каждом экране.

        Пустое поле не показывает ничего: «0 / 0» над пустым полем не
        сообщает нового — нулей и так не видно, — а очищать там нечего.
        Поэтому вся строка появляется вместе с первым знаком.

        Отступ сверху у поля (`pt-6`) при этом постоянный, а не парный
        к строке: иначе текст прыгал бы вниз на первой же букве. Размер
        крестика и этот отступ связаны: строка выше вынудит увеличить
        отступ, иначе первая строка текста уедет под счётчик.
      */}
      {!pusto && (
        <div className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 px-2 pb-1 pt-[3px]">
          {/*
            Счётчик, затем крестик у самого края. Крестик прижат к правому
            краю намеренно: это привычное место кнопки очистки, и попасть
            по нему пальцем проще, чем по элементу в середине строки.
            Порядок в разметке совпадает с порядком на экране, поэтому и
            читалка произносит их в том же порядке.

            `aria-live` у счётчика нет намеренно: он меняется на каждой
            набранной букве, и читалка перебивала бы человека посреди
            слова. Значение доступно по запросу через `aria-describedby`
            самого поля.

            Два числа через дробь без подписей: у поля ввода это читается
            однозначно, а слова «знаков» и «слов» занимали место рядом с
            крестиком и повторяли очевидное. Читалке подписи всё же нужны,
            для неё они остались в `aria-label`, скрытом от глаз.
          */}
          <span
            id={idSchyotchika}
            aria-label={t('schyot', { znakov, slov })}
            className="select-none whitespace-nowrap text-[11px] leading-none text-[var(--color-text-muted)]"
          >
            {znakov} / {slov}
          </span>

          {/* Крестик не иконка без подписи: у него есть aria-label, иначе
              на слух это «кнопка» без указания, что она делает. */}
          <button
            type="button"
            onClick={() => {
              onChange('');
            }}
            className="pointer-events-auto inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-danger-400)] hover:text-[var(--color-danger-400)] disabled:opacity-40"
            disabled={disabled}
            aria-label={t('ochistit')}
            title={t('ochistit')}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <textarea
        id={idPolya}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        // Пустое поле счётчика не рисует, и ссылка на него вела бы в
        // пустоту: W3C считает это ошибкой разметки.
        aria-describedby={pusto ? undefined : idSchyotchika}
        className={cn(
          'w-full resize-y rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-3 pb-3 pt-6 text-sm outline-none transition-colors focus:border-[var(--color-accent-500)] placeholder:text-[var(--color-text-muted)] disabled:opacity-60',
          className,
        )}
      />
    </div>
  );
}
